import cs from 'classnames';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import './hs.scss';

const FONT_URL =
  'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css';
const DESKTOP_QUERY = '(min-width: 1024px)';

let hsDocumentUsers = 0;

export interface HsShellState {
  /** 내비가 사이드바(`sidebar`)인지 상단 내비(`topnav`)인지. 상단 내비면 상단 바에 사이드바 여는 버튼이 없다 */
  layout: 'sidebar' | 'topnav';
  /** 사이드바가 지금 화면에 보이는지(데스크톱에서 펼쳐져 있으면 true). 안 보이면 상단 바에 여는 버튼을 둔다. */
  sidebarVisible: boolean;
  toggleSidebar: () => void;
}

const HsShellContext = createContext<HsShellState>({
  layout: 'sidebar',
  sidebarVisible: true,
  toggleSidebar: () => {},
});

export function useHsShell() {
  return useContext(HsShellContext);
}

/** 데스크톱인지(사이드바를 옆에 붙여 두는 너비인지) */
function useDesktop() {
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setDesktop(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return desktop;
}

/**
 * 디자인 시스템 토큰·글꼴을 문서 전체에 연다. 셸을 벗어나면(전체 화면 뷰어 등) 원래대로 돌린다.
 * 셸 없이 단독으로 그리는 화면(로그인·404 등 `HsCenterPage`)도 이것으로 토큰을 연다.
 */
export function useHsDocument(bare = false) {
  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = FONT_URL;
      document.head.appendChild(link);
    }
    const root = document.documentElement;
    // 셸 안에 단독 화면이 겹쳐 열릴 수 있어(dutch 멤버 선택 등) 연 쪽이 모두 닫혀야 토큰을 거둔다
    hsDocumentUsers += 1;
    root.classList.add('hs-root');
    // 창 안에 끼워 넣는 화면은 바탕을 창 색에 맞춘다
    if (bare) {
      root.classList.add('hs-embed');
    }
    return () => {
      hsDocumentUsers -= 1;
      if (hsDocumentUsers === 0) {
        root.classList.remove('hs-root');
      }
      if (bare) {
        root.classList.remove('hs-embed');
      }
    };
  }, [bare]);
}

function useHsRoot(drawerOpen: boolean, bare: boolean) {
  useHsDocument(bare);

  // 서랍이 열려 있는 동안 뒤 화면이 스크롤되지 않게 한다.
  useEffect(() => {
    document.documentElement.classList.toggle('hs-locked', drawerOpen);
    return () => document.documentElement.classList.remove('hs-locked');
  }, [drawerOpen]);
}

/**
 * 고정 틀(`fixed`)에서 모바일 키보드가 올라오면 보이는 영역(visualViewport)에 맞춰 앱 높이를 줄인다.
 * 그래야 아래 입력창이 키보드 바로 위에 붙고 가운데 목록만 스크롤된다.
 *
 * offsetTop은 키보드가 가린 만큼(레이아웃 높이 - 보이는 높이) 안으로만 따라간다.
 * iOS 웹뷰(당겨서 새로고침)에서 스크롤할 게 없는 화면을 끌면 문서가 튕기며 offsetTop이 그 반대로 바뀌는데,
 * 그대로 따라가면 앱이 손가락과 반대로 움직인다(chat에서 실제로 겪었다).
 */
function useVisualViewportHeight(enabled: boolean) {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!enabled || !viewport) {
      return;
    }
    const root = document.documentElement;
    const update = () => {
      const maxTop = Math.max(0, root.clientHeight - viewport.height);
      const top = Math.min(Math.max(0, viewport.offsetTop), maxTop);
      root.style.setProperty('--hs-app-height', `${viewport.height}px`);
      root.style.setProperty('--hs-app-top', `${top}px`);
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      root.style.removeProperty('--hs-app-height');
      root.style.removeProperty('--hs-app-top');
    };
  }, [enabled]);
}

function readCollapsed(key: string) {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

interface Props {
  /** 접힘 상태를 서비스별로 기억하는 키에 쓴다. */
  service: string;
  /** 왼쪽 사이드바(`HsSidebar`). 목록이 계속 늘어나는 서비스(chat 대화, comic 만화) */
  sidebar?: ReactNode;
  /**
   * 사이드바 대신 상단 내비(`HsTopNav`). 메뉴가 몇 개로 정해진 서비스.
   * 데스크톱·태블릿은 위 가로 막대, 휴대폰은 아래 탭 바가 된다
   */
  topnav?: ReactNode;
  /**
   * 화면 높이에 고정된 앱 틀. 문서는 스크롤하지 않고 페이지가 안쪽 영역만 스크롤한다.
   * 아래에 입력창이 붙는 화면(채팅 등)용. 키보드가 올라오면 보이는 높이에 맞춰 줄어든다.
   */
  fixed?: boolean;
  /**
   * 사이드바 없이 화면만 그린다. 다른 서비스의 창(iframe) 안에 끼워 넣는 화면용(예: 프로필 관리).
   * 상단 바도 그리지 않고 `hs-page`만 둔다.
   */
  bare?: boolean;
  /** 사이드바 없는 단독 화면(apps 런처). `bare`처럼 그리되 바탕은 창 색이 아니라 화면 색이다. */
  launcher?: boolean;
}

/**
 * 서비스 공통 틀: 왼쪽 사이드바 + 오른쪽 화면.
 * 데스크톱은 사이드바를 옆에 고정하고(접을 수 있다), 그보다 좁으면 왼쪽에서 밀려 나오는 서랍이 된다.
 * 화면 스크롤은 문서(window) 스크롤이라 각 페이지는 `HsTopBar` + `.hs-page`만 그리면 된다(`fixed`면 페이지가 스스로 스크롤).
 */
export default function HsShell({ service, sidebar, topnav, fixed = false, bare = false, launcher = false }: Props) {
  const storageKey = `hs.${service}.sidebar-collapsed`;
  const plain = bare || launcher || !!topnav;
  const desktop = useDesktop();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey));
  const [drawerOpen, setDrawerOpen] = useState(false);

  useHsRoot(!desktop && drawerOpen, bare);
  useVisualViewportHeight(fixed);

  // 서랍은 다른 화면으로 이동하면 닫는다.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  const toggleSidebar = () => {
    if (!desktop) {
      setDrawerOpen(!drawerOpen);
      return;
    }
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      // 저장이 막힌 환경(사생활 보호 모드 등)에서는 이번 방문 동안만 유지한다.
    }
  };

  const state: HsShellState = {
    layout: topnav ? 'topnav' : 'sidebar',
    // 상단 내비에서는 열 사이드바가 없으므로 늘 "보이는" 것으로 둔다(상단 바에 여는 버튼을 두지 않는다)
    sidebarVisible: !!topnav || (!plain && desktop && !collapsed),
    toggleSidebar,
  };

  return (
    <HsShellContext.Provider value={state}>
      <div
        className={cs('hs-app', {
          fixed,
          bare: bare || launcher,
          topnav: !!topnav && !bare,
          desktop: desktop && !plain,
          collapsed: desktop && collapsed,
          'drawer-open': !desktop && drawerOpen,
        })}
      >
        {!plain && <div className="hs-scrim" onClick={() => setDrawerOpen(false)} />}
        {!plain && <aside className="hs-sidebar">{sidebar}</aside>}
        {topnav && !bare && topnav}
        <div className="hs-main">
          <Outlet />
        </div>
      </div>
    </HsShellContext.Provider>
  );
}
